import { gridRows } from '@nsb/core';
import { useMemo, type ReactNode } from 'react';
import { bandFaint, DEVIATION_FOOT, FOOT, isPale, NOT_CONFIGURED, NOT_CONFIGURED_FOOT, ringOf } from './look.ts';
import { earlyFor, needKeys, stepName, type TilePhase, type TileStep } from './model.ts';
import { BandStrip, Tick } from './Tile.tsx';
import { docksOf, placesOf } from './transitions.ts';

/**
 * A tree on a phone (design study 09, phone A; study 12): recomposed, not shrunk (guide §20.11). The phases as white
 * blocks one under another between their start and end lines (P-17), each step a row in the order the board runs (its
 * picture, its band, its name, what it comes after, where it stands); what may happen at the same time is read from
 * the waits alone, never boxed together (P-19). A tap opens it, as a click does on a wide screen (TreeView); each row
 * carries its step's key, so going back finds it. A phase's closing gate docks between it and the next phase as on the
 * board (design study 20, drawn B, on a phone; P-17): its row in the band between the two, in the middle, the next
 * phase's title under it just above that phase's start line, the start's above the first; the steps that start the
 * phase read "as Design opens" (study 20's phone) in place of the gate's name, or of a step of an earlier phase, which
 * the board draws through the transitions. A step held back by a gate or an inspection not passed (L-17 B) reads Held at its foot, as its
 * tile does, and names what holds it on hover (`TileStep.hold`, a job's: tree/job.ts), as the tile does. A task done early
 * (design study 31, slice 4) is a row in its phase, its picture smaller, the step it is done for under its name ("For
 * Engineering"), where it stands at its foot as every row's; a tap opens its step.
 */

export function PhoneRows({
  phases,
  steps,
  label,
  onOpen,
  actionsFor,
  onPhase,
}: {
  phases: readonly TilePhase[];
  /** Its steps; a job's carry what holds one back, named for its hover (`TileStep.hold`, L-17 B). */
  steps: readonly TileStep[];
  label: string;
  onOpen?: (key: string) => void;
  actionsFor?: (key: string) => ReactNode;
  /** A phase's heading tapped: its card opened (the master's). */
  onPhase?: (phase: string) => void;
}) {
  const byKey = useMemo(() => new Map(steps.map((s) => [s.key, s])), [steps]);
  const rows = useMemo(() => {
    const keys = new Set(steps.map((s) => s.key));
    return gridRows(
      phases.map((p) => ({ key: p.key, closedBy: p.closedBy })),
      steps.map((s) => ({ key: s.key, phase: s.phase, needs: needKeys(s, keys), ...(s.end ? { end: s.end } : {}) })),
    );
  }, [phases, steps]);
  const keys = useMemo(() => new Set(steps.map((s) => s.key)), [steps]);
  // The gate docked on each phase's heading (the start on the first's): as on the board.
  const docks = useMemo(() => docksOf(phases, rows, steps), [phases, rows, steps]);
  // Where each step comes in the run of phases, and the places whose gate is docked (the board's `linesThrough`).
  const place = useMemo(() => placesOf(phases, steps, true), [phases, steps]);
  const dockedFrom = useMemo(() => {
    const real = placesOf(phases, steps);
    return new Set([...docks.values()].flatMap((g) => (real.has(g) ? [real.get(g)!] : [])));
  }, [phases, steps, docks]);
  /** A wait that comes through the transitions, as the board draws it: on a step of an earlier phase whose gate is docked. */
  const through = (s: TileStep, d: TileStep): boolean => {
    const a = d.ghost || d.dot ? undefined : place.get(d.key);
    const b = place.get(s.key);
    return a !== undefined && b !== undefined && a < b && dockedFrom.has(a);
  };
  // What a step comes after, passing through a dot to what the step left out came after.
  const comesAfter = (s: TileStep): TileStep[] =>
    needKeys(s, keys)
      .map((d) => byKey.get(d))
      .flatMap((d) => (d?.key.startsWith('out:') ? comesAfter(d) : d ? [d] : []));
  const row = (k: string, opens: { gate: string; name: string } | null = null) => {
    const s = byKey.get(k)!;
    if (s.dot) {
      // A step left out: a dot on the way down, its name crossed out; a tap opens it to put it back.
      return (
        <li key={k} data-node={k} className="list-none scroll-mt-24">
          <button type="button" onClick={onOpen ? () => onOpen(k) : undefined} aria-label={s.words} className="grid min-h-11 w-full grid-cols-[56px_minmax(0,1fr)] items-center gap-2.5 px-2 text-left">
            <span className="relative grid h-11 place-items-center">
              <span className="absolute inset-y-0 left-1/2 w-px bg-ink-3/50" />
              <span className="relative block h-[14px] w-[14px] rounded-full border-[1.5px] border-ink-3 bg-paper" />
            </span>
            <s className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3">{stepName(s.title)}</s>
          </button>
        </li>
      );
    }
    // A step that starts its phase hangs from the heading: it comes "as Design opens", not after the gate docked on it
    // or a step of an earlier phase (the board draws those through the transitions).
    const before = comesAfter(s);
    const asOpens = opens !== null && before.some((d) => through(s, d));
    const after = before.filter((d) => !d.end && !(opens !== null && through(s, d)));
    const pale = isPale(s);
    // Come late, or a gate marked done early (design study 21): ringed dashed in bengara, its foot in bengara, as on the board.
    const deviation = Boolean(s.deviation);
    const ring = ringOf(s, false, 1);
    // A task done early (design study 31, slice 4): its picture smaller, as its tile is, its step named under its name.
    const pic = s.early ? 'h-10 w-10' : 'h-14 w-14';
    const body = (
      <>
        <span className="grid h-14 w-14 shrink-0 place-items-center">
          {s.picture ? <img src={s.picture} alt="" className={`${pic} object-contain ${pale ? 'opacity-50 grayscale' : ''}`} /> : null}
        </span>
        <span className="grid min-w-0 gap-0.5">
          {s.band ? <BandStrip band={s.band} className={`w-fit max-w-full rounded-[1px] ${bandFaint(s)}`} /> : null}
          <span className={`small-caps text-[1rem] leading-snug ${pale ? 'text-ink-3' : ''}`}>{stepName(s.title)}</span>
          {s.early ? <span className="font-mono text-[10.5px] uppercase tracking-[0.05em] text-ink-2">{earlyFor(s.early.stepName)}</span> : null}
          {after.length ? (
            <span className="text-[0.8125rem] text-ink-3">
              after {after.slice(0, 2).map((a) => stepName(a.title)).join(', ')}
              {after.length > 2 ? ` and ${after.length - 2} more` : ''}
            </span>
          ) : asOpens ? (
            <span className="text-[0.8125rem] text-ink-3">as {opens!.name} opens</span>
          ) : null}
          {s.foot ? (
            <span className={`inline-flex items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.05em] ${deviation ? DEVIATION_FOOT : s.standing ? (FOOT[s.standing] ?? 'text-ink-3') : 'text-ink-3'}`}>
              {s.standing === 'done' ? <Tick /> : null}
              {s.foot}
            </span>
          ) : null}
          {/* Not configured says so in words on a phone too, never the ring alone (guide §20.8). */}
          {s.configured === false && !s.ghost ? (
            <span className={`font-mono text-[10.5px] font-medium uppercase tracking-[0.05em] ${NOT_CONFIGURED_FOOT}`}>{NOT_CONFIGURED}</span>
          ) : null}
        </span>
      </>
    );
    const frame = `grid w-full grid-cols-[56px_minmax(0,1fr)] items-center gap-2.5 rounded-[2px] border p-2 text-left ${s.ghost ? 'border-dashed border-rule-strong' : pale ? 'border-rule bg-paper' : 'border-rule-strong bg-sheet'} ${ring}`;
    return (
      <li key={k} data-node={k} className="list-none scroll-mt-24">
        {onOpen ? (
          <button type="button" className={`${frame} min-h-16`} onClick={() => onOpen(k)} aria-label={s.words} title={s.hold}>
            {body}
          </button>
        ) : (
          <div className={frame} title={s.hold}>
            {body}
          </div>
        )}
        {actionsFor?.(k) ? <div className="mt-1.5 flex gap-2 pl-[66px]">{actionsFor(k)}</div> : null}
      </li>
    );
  };
  const first = rows.groups[0]?.phase ?? null;
  // The ends in the middle: the start above the first phase (in its band when docked on it), the end below the last.
  const ends = (k: string | null) => (k ? <ul className="mx-auto my-3 w-full max-w-sm p-0">{row(k)}</ul> : null);
  return (
    <div role="group" aria-label={label}>
      {first !== null && docks.get(first) === rows.start ? null : ends(rows.start)}
      {rows.groups.map((g, i) => {
        const p = phases.find((x) => x.key === g.phase);
        // Its steps: a task done early is its step's, never one of its phase's.
        const count = g.rows.flat().filter((k) => !byKey.get(k)?.ghost && !byKey.get(k)?.early).length;
        const gate = g.phase !== null ? (docks.get(g.phase) ?? null) : null;
        const opens = gate && p ? { gate, name: p.name } : null;
        // Its gate docked in the band below it: drawn there, not in it.
        const next = rows.groups[i + 1]?.phase ?? null;
        const closer = next !== null ? (docks.get(next) ?? null) : null;
        const inIt = g.rows.map((r) => r.filter((k) => k !== closer)).filter((r) => r.length);
        return (
          <div key={g.phase ?? '<none>'}>
            {/* The band above it: the gate that opens it in the middle, its title just above its start line. */}
            {gate ? <ul className="mx-auto mb-1 mt-4 w-full max-w-sm p-0">{row(gate)}</ul> : null}
            <header className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 px-1 pb-1.5 pt-3">
              <span className="font-mono text-[12px] text-ink-3">{String(i + 1).padStart(2, '0')}</span>
              <h3 className="font-display text-[1.5rem] font-normal">
                {p && onPhase ? (
                  <button type="button" title="Open the phase" className="hit text-left decoration-1 underline-offset-4 hover:underline" onClick={() => onPhase(p.key)}>
                    {p.name}
                  </button>
                ) : (
                  (p?.name ?? 'In no phase yet')
                )}
              </h3>
              <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3">
                {count} {count === 1 ? 'step' : 'steps'}
              </span>
            </header>
            <section data-phase={g.phase ?? '<none>'} className="scroll-mt-24 border-y border-ink bg-sheet px-1 py-3">
              <div className="grid gap-2.5">
                {inIt.map((r) => (
                  <ul key={r.join()} className="m-0 grid gap-2.5 p-0">
                    {r.map((k) => row(k, opens))}
                  </ul>
                ))}
              </div>
            </section>
          </div>
        );
      })}
      {ends(rows.end)}
    </div>
  );
}
